import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from '@tanstack/react-query'
import axios from 'axios'
import { z } from 'zod'
import { Widgets } from './registry'
import { useCompetitionId } from '../common/hooks'
import { AuthError } from '../common/errors'

export type DataKeyMapping = {
  [key: string]: number
}

class DataKeyMap {
  private keyToId: Map<string, number>
  private idToKey: Map<number, string>

  constructor(mapping: DataKeyMapping) {
    this.keyToId = new Map(Object.entries(mapping))
    this.idToKey = new Map(Object.entries(mapping).map(([k, v]) => [v, k]))
  }

  getId(key: string): number | undefined {
    return this.keyToId.get(key)
  }

  getKey(id: number): string | undefined {
    return this.idToKey.get(id)
  }
}

class MetricMap {
  private keyToId: Map<string, number>
  private idToMetric: Map<number, Metric>

  constructor(metrics: Metric[]) {
    this.keyToId = new Map(
      metrics.map((metric) => [metric.key, metric.metric_id])
    )
    this.idToMetric = new Map(
      metrics.map((metric) => [metric.metric_id, metric])
    )
  }

  getId(key: string): number | undefined {
    return this.keyToId.get(key)
  }

  getMetric(id: number): Metric | undefined {
    return this.idToMetric.get(id)
  }
}

export type Metric = {
  metric_id: number
  key: string
  name: string
  short_name: string
  is_kpi: boolean
  is_achieved_below_target: boolean
  is_used_in_matches: boolean
  is_percent_metric: boolean
}

export async function getDataKeys(): Promise<DataKeyMapping> {
  return (await axios.get('/api/metrics/keys')).data
}

export async function getDataKeysMap(): Promise<DataKeyMap> {
  return new DataKeyMap((await axios.get('/api/metrics/keys')).data)
}

export async function getMetricKeys(): Promise<DataKeyMapping> {
  const metrics: Metric[] = (await axios.get('/api/metrics')).data

  // Transform array of metrics to {[key]: metric_id} mapping
  return metrics.reduce((acc, metric) => {
    acc[metric.key] = metric.metric_id
    return acc
  }, {} as DataKeyMapping)
}

export async function getMetricKeysMap(): Promise<MetricMap> {
  const metrics: Metric[] = (await axios.get('/api/metrics')).data
  return new MetricMap(metrics)
}

export async function getMetrics(competitionId?: number): Promise<Metric[]> {
  return (
    await axios.get('/api/metrics', {
      params: {
        competition_id: competitionId,
      },
    })
  ).data
}

export type MaxPeriodResponse = {
  max_period: string | null
}

export async function getMaxPeriod(params: {
  period_type: string
  subject_type: string
  subject_id?: string
  competition_id?: number
}): Promise<MaxPeriodResponse> {
  return (await axios.get('/api/metrics/max-period', { params })).data
}

export const useMetricsWithSuspense = () => {
  const competitionId = useCompetitionId()
  return useSuspenseQuery({
    queryKey: ['metrics'],
    queryFn: () => getMetrics(competitionId),
    staleTime: 120 * 1000,
  })
}

export const useMetrics = () => {
  const competitionId = useCompetitionId()
  return useSuspenseQuery({
    queryKey: ['metrics', competitionId],
    queryFn: () => getMetrics(competitionId),
    staleTime: 120 * 1000,
  })
}

export const useDataKeys = () => {
  return useQuery({
    queryKey: ['data-keys'],
    queryFn: getDataKeys,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  })
}

export const useMetricKeys = () => {
  return useQuery({
    queryKey: ['metric-keys'],
    queryFn: getMetricKeys,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  })
}

// TODO subject id is i64 so we should probably use strings too
// period, subject id, metric id, component, statistic, value
export type FlatData = Array<[string, number, number, number, number, number]>
export type DataPoint = {
  period: string
  subject: number
  metric: number
  component: number
  statistic: number
  value: number
}

export type GetMetricsParams = {
  period_type: string
  periods: string
  period_range: boolean
  subject_type: string
  subject_ids: string
  metrics: string
  components: string
  statistics: string
  competition_id?: number
}

export async function getMetricsFlatData(
  params: GetMetricsParams,
  source?: string
): Promise<DataPoint[]> {
  //await new Promise((resolve) => setTimeout(resolve, 2000))
  return (
    await axios.get('/api/metrics/flat_data', { params: { ...params, source } })
  ).data.map(
    ([period, subject, metric, component, statistic, value]: [
      string,
      number,
      number,
      number,
      number,
      number,
    ]) => ({
      period,
      subject,
      metric,
      component,
      statistic,
      value,
    })
  )
}

export interface MetricsDataParams {
  period_type: string
  period_range: boolean
  periods: string[]
  subject_type: string
  subject_ids: number[]
  metrics: string[]
  components: string[]
  statistics: string[]
  competition_id?: number
}

export const useMetricsData = (params: MetricsDataParams, source?: string) => {
  const { data: dataKeys } = useSuspenseQuery({
    queryKey: ['data-keys'],
    queryFn: getDataKeys,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const { data: metricKeys } = useSuspenseQuery({
    queryKey: ['metric-keys'],
    queryFn: getMetricKeys,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return useSuspenseQuery({
    queryKey: ['metrics-data-pure', params],
    queryFn: () => {
      const components = params.components.map((c) =>
        (dataKeys[c] || '0').toString()
      )
      const statistics = params.statistics.map((s) =>
        (dataKeys[s] || '0').toString()
      )
      const metrics = params.metrics.map((m) =>
        (metricKeys[m] || '0').toString()
      )

      const queryParams = {
        period_type: params.period_type,
        period_range: params.period_range,
        periods: params.periods.join(','),
        subject_type: params.subject_type,
        subject_ids: params.subject_ids.join(','),
        components: components.join(','),
        statistics: statistics.join(','),
        metrics: metrics.join(','),
        competition_id: params.competition_id,
      }

      return getMetricsFlatData(queryParams, source)
    },
  })
}

export type Data = {
  period: string
  component: string
  statistic: string
  metric: Metric
  subject: number
  value: number
}

export const useData = (params: MetricsDataParams, source?: string) => {
  const { data: dataKeys } = useSuspenseQuery({
    queryKey: ['data-keys-map'],
    queryFn: getDataKeysMap,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const { data: metricKeys } = useSuspenseQuery({
    queryKey: ['metric-keys-map'],
    queryFn: getMetricKeysMap,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  return useSuspenseQuery({
    queryKey: ['metrics-data', params],
    queryFn: async () => {
      const components = params.components.map((c) =>
        (dataKeys.getId(c) || '0').toString()
      )
      const statistics = params.statistics.map((s) =>
        (dataKeys.getId(s) || '0').toString()
      )
      const metrics = params.metrics.map((m) =>
        (metricKeys.getId(m) || '0').toString()
      )

      const queryParams = {
        period_type: params.period_type,
        period_range: params.period_range,
        periods: params.periods.join(','),
        subject_type: params.subject_type,
        subject_ids: params.subject_ids.join(','),
        components: components.join(','),
        statistics: statistics.join(','),
        metrics: metrics.join(','),
        competition_id: params.competition_id,
      }

      const data: DataPoint[] = await getMetricsFlatData(queryParams, source)

      return data.map(
        ({ period, subject, metric, component, statistic, value }) => ({
          period,
          subject,
          value,
          component: dataKeys.getKey(component) || '',
          statistic: dataKeys.getKey(statistic) || '',
          metric: metricKeys.getMetric(metric),
        })
      ) as Data[]
    },
  })
}

// Pages API types
export type Page = {
  page_id: number
  path: string
}

// Base widget type from API that includes children
export type WidgetWithChildren = Widgets & {
  children: string[]
}

export type Widget = WidgetWithChildren

// Pages API helpers
export async function getPages(): Promise<Page[]> {
  return (await axios.get('/api/pages')).data
}

export async function getWidgets(pageId: number): Promise<Widget[]> {
  return (await axios.get(`/api/pages/${pageId}/widgets`)).data
}

export async function createWidget(
  pageId: number,
  widget: Widget
): Promise<Widget> {
  return (await axios.post(`/api/pages/${pageId}/widgets`, widget)).data
}

export async function updateWidget<T>(
  pageId: number,
  widgetId: string,
  options: T,
  children: string[]
): Promise<Widget> {
  return (
    await axios.put(`/api/pages/${pageId}/widgets/${widgetId}`, {
      options,
      children,
    })
  ).data
}

export async function deleteWidget(
  pageId: number,
  widgetId: string
): Promise<void> {
  await axios.delete(`/api/pages/${pageId}/widgets/${widgetId}`)
}

// React Query hooks for Pages API
export const usePages = () => {
  return useQuery({
    queryKey: ['pages'],
    queryFn: () => getPages(),
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export const useWidgets = (pageId: number) => {
  return useQuery({
    queryKey: ['widgets', pageId],
    queryFn: () => getWidgets(pageId),
    staleTime: 30 * 1000, // Refresh more frequently
    gcTime: 5 * 60 * 1000,
  })
}

export const useCreateWidget = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pageId, widget }: { pageId: number; widget: Widget }) =>
      createWidget(pageId, widget),
    onSuccess: (_, variables) => {
      // Invalidate widgets list for this page
      queryClient.invalidateQueries({
        queryKey: ['widgets', variables.pageId],
      })
    },
  })
}

// Suspense-ready hooks
export const usePagesWithSuspense = () => {
  return useSuspenseQuery({
    queryKey: ['pages'],
    queryFn: () => getPages(),
    staleTime: 5 * 60 * 1000,
  })
}

export const useWidgetsWithSuspense = (pageId: number) => {
  return useSuspenseQuery({
    queryKey: ['widgets', pageId],
    queryFn: () => getWidgets(pageId),
    staleTime: 30 * 1000, // Refresh more frequently
  })
}

// Admin check API
const AdminCheckResponseSchema = z.object({
  is_admin: z.boolean(),
})

type AdminCheckResponse = z.infer<typeof AdminCheckResponseSchema>

async function checkIsAdmin(): Promise<AdminCheckResponse> {
  try {
    const response = await axios.get('/api/me/admin')
    return AdminCheckResponseSchema.parse(response.data)
  } catch (error) {
    if (error instanceof AuthError) {
      return { is_admin: false }
    }
    throw error
  }
}

export function useIsAdmin() {
  return useSuspenseQuery({
    queryKey: ['isAdmin'],
    queryFn: checkIsAdmin,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

// Metric Formatting API
export type MetricFormatting = {
  metric_id: number
  key: string
  is_percent_metric: boolean
  decimal_places: number
  percent_decimal_places: number
  group_thousands: boolean
  suffix_thousands: boolean
  suffix: string
  prefix: string
  percent_color_thresholds: number[]
  percent_colors: string[]
}

export async function getMetricFormatting(): Promise<MetricFormatting[]> {
  return (await axios.get('/api/metrics/formatting')).data
}

export const useMetricFormatting = () => {
  return useSuspenseQuery({
    queryKey: ['metric-formatting'],
    queryFn: getMetricFormatting,
    staleTime: 10 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}
